import type { WebsitePlatform } from './types';

export type PlatformGuide = {
  id: WebsitePlatform;
  label: string;
  steps: string[];
};

export const PLATFORM_GUIDES: PlatformGuide[] = [
  {
    id: 'wordpress',
    label: 'WordPress',
    steps: [
      'Sign in to your WordPress admin (usually yoursite.com/wp-admin).',
      'Open Pages → All Pages (or Posts) and edit the page you want to update.',
      'Paste text into the block editor. For HTML, add a Custom HTML block and paste the snippet.',
      'Update SEO title/meta in your SEO plugin (Yoast, Rank Math, or Site Kit) if you use one.',
      'Click Update / Publish, then view the live page.',
      'Optional: clear any caching plugin cache so visitors see the change.',
    ],
  },
  {
    id: 'wix',
    label: 'Wix',
    steps: [
      'Open your site in the Wix Editor.',
      'Select the page from the Pages menu.',
      'Add or edit a Text / Strip section and paste the copy. For HTML, use Embed Code → Embed HTML.',
      'For title and description: Settings → SEO (Google) → Edit page SEO.',
      'Click Publish.',
      'Preview on mobile with the phone icon before you leave.',
    ],
  },
  {
    id: 'squarespace',
    label: 'Squarespace',
    steps: [
      'Log in and open the page in Squarespace editing mode.',
      'Add a Text block or edit an existing one; paste your copy.',
      'For HTML snippets, add a Code block and paste the HTML.',
      'Set page title and description under Pages → page gear → SEO.',
      'Hit Save, then Done.',
    ],
  },
  {
    id: 'godaddy',
    label: 'GoDaddy',
    steps: [
      'Open GoDaddy Websites + Marketing (or your Managed WordPress) editor.',
      'Choose the page to edit.',
      'Update text sections with the draft copy.',
      'Use Settings / SEO fields for title and meta description when available.',
      'Publish or Save, then open the live URL to confirm.',
    ],
  },
  {
    id: 'webflow',
    label: 'Webflow',
    steps: [
      'Open the project in the Webflow Designer.',
      'Select the page and edit text nodes, or add an Embed element for HTML.',
      'Page settings (gear) → SEO for title and meta description.',
      'Publish to the selected domain.',
      'Check the staging or live URL on your phone.',
    ],
  },
  {
    id: 'shopify',
    label: 'Shopify',
    steps: [
      'In Shopify admin, go to Online Store → Pages (or Products / Collections as needed).',
      'Edit the page and paste text into the rich text editor.',
      'For HTML, switch to Show HTML and paste carefully.',
      'Search engine listing title and description are under the page’s Search engine listing preview.',
      'Save, then view the storefront page.',
    ],
  },
  {
    id: 'other',
    label: 'Other / custom site',
    steps: [
      'Open whatever tool you normally use to edit the site (builder, FTP, or developer handoff).',
      'Replace the matching section with the exported text or HTML.',
      'If you cannot edit the site yourself, use “Email this to my web person” with the draft attached.',
      'Publish or ask your developer to deploy.',
      'Re-run your Website Growth Review after the change is live.',
    ],
  },
];

export function getPlatformGuide(id: string | null | undefined): PlatformGuide {
  return PLATFORM_GUIDES.find((g) => g.id === id) ?? PLATFORM_GUIDES.find((g) => g.id === 'other')!;
}

/** Mailto body for handing a draft to a web developer / web person. */
export function webPersonEmailDraft(opts: {
  businessName: string;
  actionTitle: string;
  plainText: string;
  shareUrl?: string;
}): { subject: string; body: string } {
  const subject = `Website update for ${opts.businessName}: ${opts.actionTitle}`;
  const body = [
    'Hi,',
    '',
    `Please help me add this update to our website (${opts.businessName}).`,
    '',
    opts.actionTitle,
    '',
    opts.plainText,
    '',
    opts.shareUrl ? `Draft link: ${opts.shareUrl}` : '',
    '',
    'Thanks,',
  ]
    .filter((l) => l !== undefined)
    .join('\n');
  return { subject, body };
}
